<template>
  <v-card>
    <v-card-title>
      {{ $t('provisionBatch.details.title') }}: {{ batch.batchId }}
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        small
        :disabled="!summary"
        class="mr-2"
        @click="reportDialogOpen = true"
      >
        <v-icon left small>mdi-file-document-outline</v-icon>
        {{ $t('provisionBatch.details.reportDialog.generateReport') }}
      </v-btn>
      <v-btn icon @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <provision-batch-report-dialog
      v-if="summary"
      v-model="reportDialogOpen"
      :batch="batch"
      :summary="summary"
    ></provision-batch-report-dialog>

    <v-card-text>
      <v-row>
        <!-- Batch Info -->
        <v-col cols="12" md="6">
          <h3 class="mb-3">{{ $t('provisionBatch.details.batchInformation') }}</h3>
          <v-simple-table>
            <tbody>
              <tr><td><strong>{{ $t('provisionBatch.details.tenant') }}</strong></td><td>{{ batch.tenantId }}</td></tr>
              <tr><td><strong>{{ $t('provisionBatch.details.started') }}</strong></td><td>{{ formatDateTime(batch.startedAt) }}</td></tr>
              <tr><td><strong>{{ $t('provisionBatch.details.completed') }}</strong></td><td>{{ batch.completedAt ? formatDateTime(batch.completedAt) : $t('provisionBatch.details.inProgress') }}</td></tr>
              <tr><td><strong>{{ $t('provisionBatch.details.duration') }}</strong></td><td>{{ formatDuration(batch.durationSeconds) }}</td></tr>
              <tr><td><strong>{{ $t('provisionBatch.details.status') }}</strong></td><td><v-chip :color="getStatusColor(batch)" small>{{ batch.currentStatus }}</v-chip></td></tr>
              <tr><td><strong>{{ $t('provisionBatch.details.totalEvents') }}</strong></td><td>{{ batch.totalEventsCount.toLocaleString() }}</td></tr>
              <tr><td><strong>{{ $t('provisionBatch.details.purchaseInvoices') }}</strong></td><td>{{ batch.purchaseInvoicesCount }}</td></tr>
              <tr><td><strong>{{ $t('provisionBatch.details.salesInvoices') }}</strong></td><td>{{ batch.salesInvoicesCount }}</td></tr>
            </tbody>
          </v-simple-table>
          <v-alert
            v-if="!batch.success && batch.errorMessage"
            type="error"
            text
            dense
            class="mt-3"
          >{{ batch.errorMessage }}</v-alert>
        </v-col>

        <!-- Status History -->
        <v-col cols="12" md="6">
          <h3 class="mb-3">{{ $t('provisionBatch.details.statusHistory') }}</h3>
          <v-timeline dense>
            <v-timeline-item
              v-for="(entry, index) in batch.statusHistory"
              :key="index"
              small
              :color="getHistoryStatusColor(entry.status)"
            >
              <template v-slot:opposite>
                <span class="text-caption">{{ formatTime(entry.timestamp) }}</span>
              </template>
              <div>
                <div class="font-weight-bold">{{ entry.status }}</div>
                <div v-if="entry.count" class="text-caption">{{ $t('provisionBatch.details.count') }}: {{ entry.count }}</div>
                <div v-if="entry.message" class="text-caption">{{ entry.message }}</div>
              </div>
            </v-timeline-item>
          </v-timeline>
        </v-col>
      </v-row>

      <!-- Area Summary -->
      <v-row v-if="summary">
        <v-col cols="12">
          <h3 class="mb-3">{{ $t('provisionBatch.details.areaSummary') }}</h3>
          <v-data-table
            :headers="summaryHeaders"
            :items="summary.areaSummaries"
            hide-default-footer
            class="elevation-1"
            show-expand
            single-expand
            :expanded.sync="expandedRows"
          >
            <template v-slot:item.totalAcceptedCostGross="{ item }">
              {{ formatCurrency(item.totalAcceptedCostGross) }}
            </template>
            <template v-slot:item.totalReimbursementNet="{ item }">
              {{ formatCurrency(item.totalReimbursementNet) }}
            </template>
            <template v-slot:item.baseProvisionNet="{ item }">
              {{ formatCurrency(item.baseProvisionNet) }}
            </template>
            <template v-slot:item.transactionProvisionNet="{ item }">
              {{ formatCurrency(item.transactionProvisionNet) }}
            </template>
            <template v-slot:item.sourceProvisionNet="{ item }">
              {{ formatCurrency(item.sourceProvisionNet) }}
            </template>
            <template v-slot:item.totalPurchaseInvoiceAmount="{ item }">
              {{ formatCurrency(item.totalPurchaseInvoiceAmount) }}
            </template>
            <template v-slot:item.totalSalesInvoiceAmount="{ item }">
              {{ formatCurrency(item.totalSalesInvoiceAmount) }}
            </template>
            <template v-slot:item.netToTaxiCompany="{ item }">
              {{ formatCurrency(item.netToTaxiCompany) }}
            </template>

            <!-- Expanded row with details -->
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="pa-4">
                <v-row>
                  <!-- Monthly Breakdown -->
                  <v-col cols="12" md="6" v-if="item.monthlyBreakdown && item.monthlyBreakdown.length > 0">
                    <h4 class="mb-2">{{ $t('provisionBatch.details.monthlyBreakdown') }}</h4>
                    <v-simple-table dense>
                      <thead>
                        <tr>
                          <th class="text-right">{{ $t('provisionBatch.details.month') }}</th>
                          <th class="text-right">{{ $t('provisionBatch.details.amount') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="month in item.monthlyBreakdown" :key="month.monthName">
                          <td class="text-right">{{ month.monthName }}</td>
                          <td class="text-right">{{ month.eventCount.toLocaleString() }} kpl</td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr class="font-weight-bold">
                          <td class="text-right">{{ $t('provisionBatch.details.total') }}</td>
                          <td class="text-right">{{ item.eventCount.toLocaleString() }} kpl</td>
                        </tr>
                      </tfoot>
                    </v-simple-table>
                  </v-col>

                  <!-- Provision Configuration -->
                  <v-col cols="12" :md="item.monthlyBreakdown && item.monthlyBreakdown.length > 0 ? 6 : 12" v-if="item.provisionConfigurations && item.provisionConfigurations.length > 0">
                    <h4 class="mb-2">{{ $t('provisionBatch.details.provisionConfiguration') }}</h4>
                    <v-list dense>
                      <v-list-item v-for="(config, index) in item.provisionConfigurations" :key="index" class="px-0">
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-bold">
                            {{ config.provisionType }}: {{ formatPercentage(config.percentage) }}
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            {{ config.description }}
                          </v-list-item-subtitle>
                          <v-list-item-subtitle class="text-caption">
                            {{ $t('provisionBatch.details.calculationBase') }}: {{ config.calculationBase }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-col>

                  <!-- No data message -->
                  <v-col cols="12" v-if="(!item.monthlyBreakdown || item.monthlyBreakdown.length === 0) && (!item.provisionConfigurations || item.provisionConfigurations.length === 0)">
                    <v-alert type="info" text dense>
                      {{ $t('provisionBatch.details.noDataAvailable') }}
                    </v-alert>
                  </v-col>
                </v-row>
              </td>
            </template>

            <!-- Footer with totals -->
            <template v-slot:body.append>
              <tr class="font-weight-bold grey lighten-4">
                <td></td>
                <td>{{ $t('provisionBatch.details.total') }}</td>
                <td class="text-right">{{ computedTotalEvents }}</td>
                <td class="text-right">{{ formatCurrency(summary.totals.totalAcceptedCostGross) }}</td>
                <td class="text-right">{{ formatCurrency(summary.totals.totalReimbursementNet) }}</td>
                <td class="text-right">{{ formatCurrency(summary.totals.totalBaseProvision) }}</td>
                <td class="text-right">{{ formatCurrency(summary.totals.totalTransactionProvision) }}</td>
                <td class="text-right">{{ formatCurrency(summary.totals.totalSourceProvision) }}</td>
                <td class="text-right">{{ formatCurrency(summary.totals.totalPurchaseInvoiceAmount) }}</td>
                <td class="text-right">{{ formatCurrency(summary.totals.totalSalesInvoiceAmount) }}</td>
                <td class="text-right">{{ formatCurrency(summary.totals.netToTaxiCompany) }}</td>
                <td></td>
                <td></td>
              </tr>
            </template>
          </v-data-table>
        </v-col>
      </v-row>

      <!-- Download Links -->
      <v-row v-if="batch.jsonSummaryFileName || batch.eventIdsFileName || batch.detailedReportFileName">
        <v-col cols="12">
          <h3 class="mb-3">{{ $t('provisionBatch.details.downloadReports') }}</h3>
          <v-btn v-if="batch.jsonSummaryFileName" color="primary" class="mr-2" @click="downloadFile(batch.jsonSummaryFileName)">
            <v-icon left>mdi-download</v-icon>
            {{ $t('provisionBatch.details.jsonSummary') }}
          </v-btn>
          <v-btn v-if="batch.eventIdsFileName" color="primary" class="mr-2" @click="downloadFile(batch.eventIdsFileName)">
            <v-icon left>mdi-download</v-icon>
            {{ $t('provisionBatch.details.eventIds') }}
          </v-btn>
          <v-btn v-if="batch.detailedReportFileName" color="primary" class="mr-2" @click="downloadFile(batch.detailedReportFileName)">
            <v-icon left>mdi-download</v-icon>
            {{ $t('provisionBatch.details.detailedJson') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import moment from "moment";
import { mapActions } from "vuex";
import ProvisionBatchReportDialog from "./ProvisionBatchReportDialog.vue";

export default {
    name: "ProvisionBatchDetails",
    components: {
        ProvisionBatchReportDialog
    },
    props: {
        batch: {
            type: Object,
            required: true
        },
        summary: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            expandedRows: [],
            reportDialogOpen: false
        };
    },
    computed: {
        summaryHeaders() {
            return [
                { text: this.$t("provisionBatch.details.area"), value: "area", align: "end" },
                { text: this.$t("provisionBatch.details.events"), value: "eventCount", align: "end" },
                { text: this.$t("provisionBatch.details.acceptedCostGross"), value: "totalAcceptedCostGross", align: "end" },
                { text: this.$t("provisionBatch.details.reimbursementNet"), value: "totalReimbursementNet", align: "end" },
                { text: this.$t("provisionBatch.details.baseProvision"), value: "baseProvisionNet", align: "end" },
                { text: this.$t("provisionBatch.details.transactionProvision"), value: "transactionProvisionNet", align: "end" },
                { text: this.$t("provisionBatch.details.sourceProvision"), value: "sourceProvisionNet", align: "end" },
                { text: this.$t("provisionBatch.details.purchaseInvTotal"), value: "totalPurchaseInvoiceAmount", align: "end" },
                { text: this.$t("provisionBatch.details.salesInvTotal"), value: "totalSalesInvoiceAmount", align: "end" },
                { text: this.$t("provisionBatch.details.netToCompany"), value: "netToTaxiCompany", align: "end" },
                { text: this.$t("provisionBatch.details.purchaseInvoiceNumber"), value: "purchaseInvoiceNumber", align: "end" },
                { text: this.$t("provisionBatch.details.salesInvoiceNumber"), value: "salesInvoiceNumber", align: "end" }
            ];
        },
        computedTotalEvents() {
            if (!this.summary || !this.summary.areaSummaries) return 0;
            return this.summary.areaSummaries.reduce((sum, area) => sum + area.eventCount, 0);
        }
    },
    methods: {
        ...mapActions("provisionBatch", [
            "downloadBatchSummary",
            "downloadBatchEventIds",
            "downloadBatchDetailedReport"
        ]),

        async downloadFile(fileName) {
            try {
                let blob;
                const batchId = this.batch.batchId;

                if (fileName === this.batch.jsonSummaryFileName) {
                    blob = await this.downloadBatchSummary(batchId);
                } else if (fileName === this.batch.eventIdsFileName) {
                    blob = await this.downloadBatchEventIds(batchId);
                } else if (fileName === this.batch.detailedReportFileName) {
                    blob = await this.downloadBatchDetailedReport(batchId);
                } else {
                    return;
                }

                const url = window.URL.createObjectURL(new Blob([blob]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (error) {
                // Download failed silently
            }
        },

        formatDateTime(dateString) {
            return moment(dateString).format("YYYY-MM-DD HH:mm:ss");
        },
        formatTime(dateString) {
            return moment(dateString).format("HH:mm:ss");
        },
        formatDuration(seconds) {
            if (!seconds) return "-";
            const minutes = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${minutes}m ${secs}s`;
        },
        formatCurrency(value) {
            return new Intl.NumberFormat("fi-FI", {
                style: "currency",
                currency: "EUR"
            }).format(value);
        },
        formatPercentage(value) {
            return new Intl.NumberFormat("fi-FI", {
                style: "percent",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(value / 100);
        },
        getStatusColor(batch) {
            if (batch.success) return "success";
            if (batch.currentStatus === "Failed") return "error";
            if (batch.currentStatus === "Completed") return "success";
            return "info";
        },
        getHistoryStatusColor(status) {
            if (status === "Completed") return "success";
            if (status === "Failed") return "error";
            if (status.includes("Created") || status.includes("Sent")) return "primary";
            return "grey";
        }
    }
};
</script>
